import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';

import AlertDialog from '../Dialog/AlertDialog';
import useOnboardingDialog from '../../utils/useOnboardingDialog';
import useRouter from '../../utils/useRouter';
import { shouldDisplayHintsAndOnboardingDialogs } from '../../utils/shouldDisplayHintsAndOnboardingDialogs';
import { selectUser } from '../../reducers/user.selectors';
import { campaign, onboardingHints, routes } from '../../constants';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../services/track.service';
import { spaces } from '../../assets/styles/themes/tokens';

const AiOnlyWelcomeDialog = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const { isShown, markDialogAsSeen } = useOnboardingDialog(onboardingHints.aiOnlyWelcomeDialog);

  const { match } = useRouter();
  const currentPath = match.location.pathname;
  const isAllowedPage = useMemo(
    () => currentPath.startsWith(`/${routes.aiTools.root}`),
    [currentPath]
  );

  const shouldShowDialog = useMemo(() => {
    if (!user?.isAiOnlyUser) return false;
    if (!shouldDisplayHintsAndOnboardingDialogs()) return false;
    return isAllowedPage && !isShown;
  }, [user?.isAiOnlyUser, isAllowedPage, isShown]);

  const closeDialog = () => {
    ampliTrackMessageDisplayedEvent(campaign.aiOnlyWelcomeMessage, MessageType.DIALOG, 1);
    markDialogAsSeen();
  };

  if (!shouldShowDialog) return null;

  return (
    <AlertDialog
      variant='text'
      open={!isShown}
      onClose={closeDialog}
      title={`${t('Welcome to meinUnterricht KI, {{firstName}}', { firstName: user?.firstName })} 👋`}
      description={
        <>
          {t('aiOnlyWelcomeIntro')}
          <Box component='ul' sx={{ listStyle: 'none', paddingLeft: spaces.sm, marginTop: spaces.sm, marginBottom: 0 }}>
            <li>{t('aiOnlyWelcomeBullet1')}</li>
            <li>{t('aiOnlyWelcomeBullet2')}</li>
            <li>{t('aiOnlyWelcomeBullet3')}</li>
          </Box>
        </>
      }
      primaryButtonText={t('Get started now')}
      onPrimaryClick={closeDialog}
    />
  );
};

export default AiOnlyWelcomeDialog;
